import * as React from 'react';
import { Box, Flex } from '@ttoss/ui';
import { DEFAULT_STEP_OPTIONS } from '../../constants/onboarding';
import {
  RecommendationsCharts,
  RecommendationsChartsProps,
} from '../RecommendationCharts/RecommendationsCharts';
import { Steps } from 'intro.js-react';
import { useNavigation } from '../../providers/NavigationProvider/NavigationProvider';
import MapWithSearch, {
  LatLng,
  MapWithSearchProps,
} from '../MapWithSearch/MapWithSearch';
import PlantDetailsCard, {
  PlantDetailsCardProps,
} from '../PlantDetailsCard/PlantDetailsCard';
import ToggleButton from '../ToggleButton/ToggleButton';

export type PlantationType = 'iLPF' | 'Mono';

export type RecommendationsProps = {
  initialLocation?: MapWithSearchProps['initialLocation'];
  recommendations: PlantDetailsCardProps['recommendations'];
  recommendationsChartsProps: RecommendationsChartsProps;
  onLatLng: (latLng: LatLng) => void;
  onChangePlantationType: (plantationType: PlantationType) => void;
};

export const ONBOARD_KEY = 'onboarding:Recommendations';

const Recommendations = ({
  initialLocation,
  onLatLng,
  onChangePlantationType,
  recommendations,
  recommendationsChartsProps,
}: RecommendationsProps) => {
  const { popUpSpecialistWasClosed, onFinishOnboard } = useNavigation();

  const [state, setState] = React.useState({
    stepsEnabled: false,
    initialStep: 0,
    steps: [
      {
        element: '.select-species',
        intro:
          'Serão essas espécies (e seus clones) que serão indicadas pelo Siflor Cerrado para sua localização, então fique a tento a coloração e legenda das espécies/clones recomendada a você',
      },
      {
        element: '.toggle-button',
        intro:
          'Este é o botão de seleção de sistema de cultivo, selecione o sistema mais indicado para você',
      },
      {
        element: '.specie-apto-superior',
        intro:
          'Espécies/clones que apresentam produtividade acima da média definida para a região, boas condições de homogeneidade e boa qualidade de fuste, não apresentando registros e doenças bióticas e/ou abióticas que comprometam significativamente a produtividade.',
      },
      {
        element: '.specie-apto',
        intro:
          'Espécies/clones indicada para cultivo, por apresentar produtividade madeireira em torno da média da região e não apresentar registros de doenças bióticas e/ou abióticas que comprometam significativamente a produtividade.',
        position: 'right',
      },
      {
        element: '.specie-apto-com-ressalvas',
        intro:
          'Espécies/clones que apresentam potencial produtivo, mas que, em algumas condições, são afetadas negativamente por estresses bióticos e abióticos, podendo ser manejados sem comprometer o cultivo. Por exemplo, a suscetibilidade à quebra por ventos e a sensibilidade à adubação ou quando a espécie/clone foi amostrada em poucas parcelas.',
        position: 'top',
      },
      {
        element: '.specie-inapto',
        intro:
          'Espécies/clones que se enquadram em uma ou mais das seguintes condições que prejudicam a produtividade madeireira: elevada taxa de mortalidade, presença de pragas, doenças e estresses abióticos provenientes de inaptidão ao clima e solo.',
        position: 'right',
      },
      {
        element: '.first-specie-item-badge',
        intro:
          'Clicando na espécie/clone desejada você pode obter mais informações',
      },
      {
        element: '.chart-container',
        intro:
          'Você também pode encontrar características climáticas sobre seu local',
      },
      {
        element: '.badge-menu-biophysics',
        intro:
          'Agora vamos conhecer um pouco mais das características biofísicas do local selecionado',
      },
    ],
  });

  React.useEffect(() => {
    const showOnboarding = !localStorage.getItem(ONBOARD_KEY);

    if (popUpSpecialistWasClosed && showOnboarding && !!initialLocation) {
      setTimeout(() => {
        setState((prev) => {
          return { ...prev, stepsEnabled: true };
        });
      }, 1000);
    }
  }, [popUpSpecialistWasClosed, initialLocation]);

  const onExit = () => {
    setState((prev) => {
      return { ...prev, stepsEnabled: false };
    });

    onFinishOnboard();

    localStorage.setItem(ONBOARD_KEY, 'showed');
  };

  return (
    <Flex sx={{ display: 'flex', flexDirection: ['column', null, 'row'] }}>
      <Steps
        enabled={state.stepsEnabled}
        steps={state.steps}
        initialStep={state.initialStep}
        onExit={onExit}
        options={DEFAULT_STEP_OPTIONS}
      />

      <MapWithSearch
        initialLocation={initialLocation}
        selectedSubMenu="recommendation"
        sx={{
          position: 'relative',
          height: ['62vh', '100vh'],
        }}
        onLatLng={onLatLng}
      >
        <ToggleButton
          initialSelected="left"
          leftButton={{
            label: 'iLPF' as PlantationType,
            onClick: () => {
              return onChangePlantationType('iLPF');
            },
          }}
          rightButton={{
            label: 'Mono' as PlantationType,
            onClick: () => {
              return onChangePlantationType('Mono');
            },
          }}
          sx={{
            position: 'absolute',
            bottom: [9, 8],
            left: 7,
            transform: 'translateY(-50%)',
          }}
        />
      </MapWithSearch>
      <Box
        sx={{
          overflowY: ['visible', null, 'scroll'],
          minWidth: ['100%', null, 500],
          paddingX: [0, 0, 6],
          maxHeight: '100vh',
        }}
      >
        <PlantDetailsCard
          currentLocation={initialLocation}
          species={[
            { name: 'Eucalipto', selected: true },
            { name: 'Pinus', selected: true },
            { name: 'Cedro Australiano', selected: true },
            { name: 'Mogno Africano', selected: true },
            { name: 'Teca', selected: true },
          ]}
          recommendations={recommendations}
        />
        <Box sx={{ marginTop: 7, paddingX: 5 }}>
          <RecommendationsCharts {...recommendationsChartsProps} />
        </Box>
      </Box>
    </Flex>
  );
};

export default Recommendations;
